/**
 * Model Finder Common — shared, DOM-only utilities for the finder views.
 *
 * After the `/datte` refactor (Phase 6) this module holds NO network code: the
 * old `ProgressiveDataLoader` (the `/io` cascade transport, with its
 * `callCached` in-memory cache) and the `AjaxResponse` DTO are gone — the
 * catalog is fetched by `data/tree-service.ts` over the content-addressed
 * `/datte/mf` endpoints. What remains is the view-side helpers:
 *   - `LoadingStateManager` — loading-indicator bookkeeping;
 *   - `ModelFinderUtils` — `normalize` (accent/case-folded name matching for
 *     the search filter), `getParentValue`, `debounce`, `showError`, etc.
 *
 * The node type is the wire `Node` (data/wire.ts); `ModelFinderNode` is kept as
 * an alias so the view files' imports stay stable.
 *
 * @see docs/speed-optimization/research/2026-06-06 - datte-model-finder-endpoints/spec.md §3.5
 */

import type { Node } from './data/wire';

/** The catalog node type the views render. Wire == internal (D7). */
export type ModelFinderNode = Node;

/* ============================================================================
 * Loading State Manager
 * Handles loading indicators during async requests
 * ========================================================================== */

export class LoadingStateManager {
  private _loadingStates: Map<string, boolean>;

  constructor() {
    this._loadingStates = new Map();
  }

  /** Set loading state for an element. */
  public setLoading(element: HTMLElement, isLoading: boolean, key?: string): void {
    const stateKey = key || element.id || Math.random().toString();
    this._loadingStates.set(stateKey, isLoading);

    if (isLoading) {
      element.classList.add('is-loading');
      if (element instanceof HTMLInputElement || element instanceof HTMLSelectElement) {
        element.disabled = true;
      }
    } else {
      element.classList.remove('is-loading');
      if (element instanceof HTMLInputElement || element instanceof HTMLSelectElement) {
        element.disabled = false;
      }
    }
  }

  /** Check if element is loading. */
  public isLoading(key: string): boolean {
    return this._loadingStates.get(key) || false;
  }

  /** Clear all loading states. */
  public clearAll(): void {
    this._loadingStates.clear();
  }
}

/* ============================================================================
 * Model Finder Utils
 * Shared utility functions
 * ========================================================================== */

export class ModelFinderUtils {
  /** Normalize string for comparison (lowercase, remove accents). */
  public static normalize(s: string): string {
    return (s || '')
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '');
  }

  /** Get parent value for a cascading level. */
  public static getParentValue(dropdowns: HTMLSelectElement[], level: number): string | null {
    if (level <= 1) return null;
    const prev = dropdowns[level - 2];
    return prev ? prev.value || null : null;
  }

  /**
   * Highlight first visible item in a list.
   * @deprecated Use DropdownUtils.highlightFirstVisible from dropdown-enhancer instead
   */
  public static highlightFirstVisible(list: HTMLElement): void {
    const firstVisible = list.querySelector(
      '.datte-mf-ssr-combo-option:not(.datte-mf-ssr-option-hidden), ' +
        '.datte-mf-page-combo-option:not(.datte-mf-page-option-hidden), ' +
        '.datte-dropdown-option:not(.datte-dropdown-option-hidden)'
    ) as HTMLElement;

    if (firstVisible) {
      list.querySelectorAll('.datte-is-active').forEach((n) => n.classList.remove('datte-is-active'));
      list
        .querySelectorAll('[aria-selected="true"]')
        .forEach((n) => n.setAttribute('aria-selected', 'false'));

      firstVisible.classList.add('datte-is-active');
      firstVisible.setAttribute('aria-selected', 'true');
    }
  }

  /** Debounce function calls. */
  public static debounce<T extends (...args: any[]) => any>(
    func: T,
    wait: number
  ): (...args: Parameters<T>) => void {
    let timeout: ReturnType<typeof setTimeout> | null = null;
    return function (...args: Parameters<T>) {
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  }

  /**
   * Show an error message near an element (small auto-dismissing tooltip).
   *
   * @param element Element to show the error near
   * @param message Error message to display
   * @param duration How long to show it (ms); 0 = until manually removed
   * @returns The error element (so it can be removed early)
   */
  public static showError(element: HTMLElement, message: string, duration: number = 5000): HTMLElement {
    const existingError = element.parentElement?.querySelector('.datte-mf-error-message');
    if (existingError) existingError.remove();

    const errorEl = document.createElement('div');
    errorEl.className = 'datte-mf-error-message';
    errorEl.textContent = message;
    errorEl.style.cssText = `
      position: absolute;
      background: #dc3545;
      color: white;
      padding: 8px 12px;
      border-radius: 4px;
      font-size: 14px;
      z-index: 1000;
      margin-top: 4px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.2);
      animation: slideDown 0.3s ease-out;
    `;

    if (!document.querySelector('#datte-mf-error-animation')) {
      const style = document.createElement('style');
      style.id = 'datte-mf-error-animation';
      style.textContent = `
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `;
      document.head.appendChild(style);
    }

    element.parentElement?.insertBefore(errorEl, element.nextSibling);

    if (duration > 0) {
      setTimeout(() => {
        if (errorEl.parentElement) {
          errorEl.style.animation = 'slideDown 0.3s ease-out reverse';
          setTimeout(() => errorEl.remove(), 300);
        }
      }, duration);
    }

    return errorEl;
  }

  /** Remove an error message from an element. */
  public static removeError(element: HTMLElement): void {
    const errorEl = element.parentElement?.querySelector('.datte-mf-error-message');
    if (errorEl) errorEl.remove();
  }
}
